import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { validatePassword } from "@/lib/validations";
import { handleError } from "@/lib/errorHandling";
import { verifyRecoveryToken, resetPasswordWithToken } from "@/services/authService";

export default function RecoveryPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    async function doVerify() {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        setVerifyError("Link inválido: token ausente.");
        return;
      }

      try {
        const resp = await verifyRecoveryToken(token);
        if (resp.success) {
          setTokenValid(true);
          setVerifyError(null);
        } else {
          setTokenValid(false);
          setVerifyError(resp.error?.message || "Token inválido ou expirado.");
        }
      } catch (err) {
        const info = handleError(err);
        setTokenValid(false);
        setVerifyError(info.message || "Falha ao verificar token.");
      } finally {
        setVerifying(false);
      }
    }
    doVerify();
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const passValidation = validatePassword(password);
    if (!passValidation.isValid) {
      setSubmitError(passValidation.message || "Senha fraca");
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError("As senhas não coincidem");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await resetPasswordWithToken(token, password);
      if (!resp.success) {
        setSubmitError(resp.error?.message || "Não foi possível redefinir a senha");
        return;
      }
      setSuccessMessage("Senha redefinida com sucesso. Você já pode fazer login.");
      // Opcional: redirecionar após alguns segundos
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      const info = handleError(err);
      setSubmitError(info.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Recuperação de senha</CardTitle>
          <CardDescription>
            {verifying
              ? "Validando seu link de recuperação..."
              : tokenValid
              ? "Defina uma nova senha para sua conta."
              : "O link de recuperação é inválido ou expirou."}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!verifying && tokenValid && (
            <form onSubmit={onSubmit} className="space-y-4">
              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              {successMessage && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">Nova senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  required
                  minLength={6}
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                  required
                  minLength={6}
                  disabled={submitting}
                />
              </div>

              <CardFooter className="px-0 flex gap-2 justify-end">
                <Button type="button" variant="outline" asChild disabled={submitting}>
                  <Link to="/">Cancelar</Link>
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Salvando..." : "Redefinir senha"}
                </Button>
              </CardFooter>
            </form>
          )}

          {!verifying && tokenValid === false && (
            <div className="space-y-4">
              {verifyError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{verifyError}</AlertDescription>
                </Alert>
              )}
              <Button asChild>
                <Link to="/">Voltar ao início</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
